import { useState } from "react";
import { alpha, useTheme } from "@mui/material/styles";
import DSBox from "views/system-design/DSBox";
import DSTypography from "views/system-design/DSTypography";
import DSIconButton from "views/system-design/DSIconButton";
import DSTextField from "views/system-design/DSTextField";
import DSAvatar from "views/system-design/DSAvatar";
import DeleteIcon from "@mui/icons-material/Delete";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import DSChip from "views/system-design/DSChip";
import { Ticket, LABEL_COLORS } from "../../types";
import { getInitials, getAvatarColor, resolveUserName } from "../../utils";
import { Author } from "api/author";

interface KlydeaCardProps {
  ticket: Ticket;
  onClick: () => void;
  onTitleUpdate: (ticketId: string, title: string) => void;
  onDelete: (ticketId: string) => void;
  users: Author[];
}

export default function KlydeaCard({
  ticket,
  onClick,
  onTitleUpdate,
  onDelete,
  users,
}: KlydeaCardProps) {
  const theme = useTheme();
  const [titleEditing, setTitleEditing] = useState<{ draft: string } | null>(
    null,
  );

  const handleTitleDoubleClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    setTitleEditing({ draft: ticket.title });
  };

  const commitTitleEdit = () => {
    if (
      titleEditing &&
      titleEditing.draft.trim() &&
      titleEditing.draft !== ticket.title
    ) {
      onTitleUpdate(ticket.id, titleEditing.draft.trim());
    }
    setTitleEditing(null);
  };

  const handleDeleteClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    if (!window.confirm("Are you sure you want to delete this idea?")) return;
    onDelete(ticket.id);
  };

  return (
    <DSBox
      onClick={() => {
        if (!titleEditing) onClick();
      }}
      sx={{
        position: "relative",
        bgcolor: alpha(theme.palette.info.light, 0.12),
        borderRadius: 2,
        p: 2,
        boxShadow: 3,
        borderLeft: 4,
        borderColor: "info.light",
        cursor: "pointer",
        "&:hover": { boxShadow: 6 },
        "&:hover .klydea-delete": { opacity: 1 },
        transition: "box-shadow 0.2s",
      }}
    >
      <DSIconButton
        className="klydea-delete"
        size="small"
        aria-label="Delete idea"
        onClick={handleDeleteClick}
        sx={{
          position: "absolute",
          top: 4,
          right: 4,
          opacity: 0,
          transition: "opacity 0.15s",
          color: "error.main",
          "&:focus-visible": { opacity: 1 },
          "&:hover": {
            bgcolor: alpha(theme.palette.error.main, 0.1),
          },
        }}
      >
        <DeleteIcon sx={{ fontSize: 16 }} />
      </DSIconButton>

      {titleEditing ? (
        <DSTextField
          value={titleEditing.draft}
          onChange={(event) =>
            setTitleEditing((prev) =>
              prev ? { draft: event.target.value } : prev,
            )
          }
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              commitTitleEdit();
            }
            if (event.key === "Escape") {
              setTitleEditing(null);
            }
          }}
          onBlur={commitTitleEdit}
          onClick={(event) => event.stopPropagation()}
          autoFocus
          size="small"
          multiline
          fullWidth
          sx={{
            mb: 1,
            "& .MuiInputBase-input": { fontSize: 14, p: 0.5 },
          }}
        />
      ) : (
        <DSTypography
          variant="body2"
          sx={{
            fontWeight: 600,
            mb: 1,
            pr: 3,
            wordBreak: "break-word",
          }}
          onDoubleClick={handleTitleDoubleClick}
        >
          {ticket.title}
        </DSTypography>
      )}

      {ticket.description && (
        <DSTypography
          variant="caption"
          sx={{
            color: "text.secondary",
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            mb: 1,
          }}
        >
          {ticket.description}
        </DSTypography>
      )}

      {ticket.labels.length > 0 && (
        <DSBox sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", mb: 1 }}>
          {ticket.labels.map((label) => (
            <DSChip
              key={label}
              label={label}
              size="small"
              sx={{
                height: 18,
                fontSize: 10,
                fontWeight: 700,
                bgcolor: LABEL_COLORS[label] ?? "grey.500",
                color: "common.white",
              }}
            />
          ))}
        </DSBox>
      )}

      <DSBox
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mt: 0.5,
        }}
      >
        <DSBox sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
          {ticket.creatorId && (
            <DSBox sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <DSAvatar
                sx={{
                  width: 20,
                  height: 20,
                  fontSize: 9,
                  fontWeight: 600,
                  bgcolor: getAvatarColor(
                    resolveUserName(ticket.creatorId, users),
                  ),
                }}
              >
                {getInitials(resolveUserName(ticket.creatorId, users))}
              </DSAvatar>
              <DSTypography
                variant="caption"
                sx={{ color: "text.secondary", fontSize: 11 }}
              >
                Reporter: {resolveUserName(ticket.creatorId, users)}
              </DSTypography>
            </DSBox>
          )}
          {ticket.assignee && (
            <DSBox sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <DSAvatar
                sx={{
                  width: 20,
                  height: 20,
                  fontSize: 9,
                  fontWeight: 600,
                  bgcolor: getAvatarColor(
                    resolveUserName(ticket.assignee, users),
                  ),
                }}
              >
                {getInitials(resolveUserName(ticket.assignee, users))}
              </DSAvatar>
              <DSTypography
                variant="caption"
                sx={{ color: "info.main", fontSize: 11 }}
              >
                Tagged: {resolveUserName(ticket.assignee, users)}
              </DSTypography>
            </DSBox>
          )}
        </DSBox>

        {ticket.comments.length > 0 && (
          <DSBox
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.25,
              color: "text.secondary",
            }}
          >
            <ChatBubbleOutlineIcon sx={{ fontSize: 14 }} />
            <DSTypography variant="caption" sx={{ fontSize: 11 }}>
              {ticket.comments.length}
            </DSTypography>
          </DSBox>
        )}
      </DSBox>
    </DSBox>
  );
}
